import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already running as standalone PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('at1s_pwa_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection — multiple methods for reliability
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isiOS = /iPhone|iPad|iPod/.test(ua)
      || /iPhone|iPad|iPod/.test(platform)
      || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // Show banner after a short delay on iOS
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome — listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // If no prompt fires after 3 seconds on mobile, show generic banner
    const isMobile = /Android|webOS|Mobile|tablet/i.test(ua);
    let fallbackTimer;
    if (isMobile) {
      fallbackTimer = setTimeout(() => {
        if (!deferredPrompt) setShowBanner(true);
      }, 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShowBanner(false);
      setDeferredPrompt(null);
    } else {
      // No native prompt — show manual guide
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('at1s_pwa_dismissed', String(Date.now()));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install banner */}
      <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96" data-testid="pwa-install-banner">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl shadow-2xl p-4 flex items-center gap-3">
          <div className="bg-white/20 rounded-lg p-2 shrink-0">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Install AT1S App</p>
            <p className="text-xs text-blue-200">
              {isIOS ? 'Add to your Home Screen from Safari' : 'Add to your home screen for quick access'}
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleInstall}
            className="bg-white text-blue-900 hover:bg-blue-50 font-bold shrink-0"
            data-testid="pwa-install-btn"
          >
            {isIOS ? 'How?' : 'Install'}
          </Button>
          <button onClick={handleDismiss} className="text-blue-300 hover:text-white shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Step-by-step Guide Modal — iOS & Android */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={handleDismiss}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-5 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ios-install-guide">
            <div className="text-center">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <span className="text-white text-sm font-black">AT1S</span>
              </div>
              <h3 className="text-xl font-bold text-slate-800">Install AT1S App</h3>
            </div>

            {isIOS ? (
              <div className="space-y-4">
                <p className="text-xs text-amber-600 font-medium text-center">Must use Safari browser on iPhone</p>
                <GuideStep num={1} text="Tap the Share button" sub="Bottom center of Safari — square with arrow pointing up" icon={
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-sky-600" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="4" y="10" width="16" height="12" rx="2" /><path d="M12 2v12" /><path d="M8 6l4-4 4 4" /></svg>
                } />
                <GuideStep num={2} text='Scroll down and tap "Add to Home Screen"' icon={
                  <div className="flex items-center gap-2 bg-sky-50 border border-sky-300 rounded-lg px-2 py-1">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-sky-600" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                    <span className="text-[11px] font-bold text-sky-700">Add to Home Screen</span>
                  </div>
                } />
                <GuideStep num={3} text='Tap "Add" in the top right' sub="The AT1S icon appears on your home screen!" color="green" />
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-green-600 font-medium text-center">Using Chrome on Android</p>
                <GuideStep num={1} text="Tap the three dots menu" sub="Top right corner of Chrome" icon={
                  <div className="flex flex-col items-center gap-1 border-2 border-green-400 rounded-lg p-2">
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                    <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                  </div>
                } />
                <GuideStep num={2} text='Tap "Install App" or "Add to Home Screen"' icon={
                  <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-lg px-2 py-1">
                    <Download className="w-4 h-4 text-green-600" />
                    <span className="text-[11px] font-bold text-green-700">Install App</span>
                  </div>
                } />
                <GuideStep num={3} text='Tap "Install" to confirm' sub="The AT1S icon appears on your home screen!" color="green" />
              </div>
            )}

            <Button onClick={handleDismiss} className="w-full bg-blue-900 hover:bg-blue-800" data-testid="ios-guide-close">Got it</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;

const GuideStep = ({ num, text, sub, icon, color = 'sky' }) => (
  <div className={`flex items-start gap-4 rounded-xl p-4 ${color === 'green' ? 'bg-green-50' : 'bg-slate-50'}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${color === 'green' ? 'bg-green-500' : 'bg-sky-500'}`}>
      <span className="text-white text-sm font-bold">{num}</span>
    </div>
    <div className="flex-1">
      <p className="font-semibold text-slate-800">{text}</p>
      {sub && <p className="text-sm text-slate-600 mt-0.5">{sub}</p>}
      {icon && <div className="mt-2">{icon}</div>}
    </div>
  </div>
);
